<template>
  <ak-loading
    v-model:active="wizardLoading"
    :is-full-page="true"
    :can-cancel="false"
  />

  <ak-sidebar-layout
    ref="sidebar"
    class="create-app bg-grey-lightest"
    size="huge"
    sidebar-style="light"
  >
    <template #sidebar>
      <ak-box>
        <ak-form ref="sidebarForm">
          <h4 class="color-grey">Create Page</h4>
          <ak-field label="Page Name" required>
            <ak-popover ref="popper" open-once placement="right" class="block">
              <template #trigger>
                <ak-input
                  ref="titleInput"
                  v-model="app.title"
                  required
                  :custom-rules="customValidationRules"
                />
              </template>
              <template #popover>
                <ak-pack>
                  <ak-pack-item>
                    <ak-icon size="huge" type="bulb" color="quaternary" />
                  </ak-pack-item>
                  <ak-pack-item>
                    Start by naming your page and assigning it to a project.
                  </ak-pack-item>
                </ak-pack>
              </template>
            </ak-popover>
          </ak-field>

          <template v-if="!wizardLoading">
            <ak-project-form-select
              v-model:id="app.campaign"
              v-model:label="app.campaignLabel"
            />
          </template>

          <a
            v-if="!showAdvancedOptions"
            class="text-small cursor-pointer"
            @click="toggleAdvanced"
          >
            <strong>[+] Show Advanced</strong>
          </a>
          <a
            v-if="showAdvancedOptions"
            class="text-small cursor-pointer"
            @click="toggleAdvanced"
          >
            <strong>[-] Hide Advanced</strong>
          </a>

          <template v-if="showAdvancedOptions">
            <div class="flex mt-small">
              <ak-field label="Start Date">
                <template #helper>Optional start date</template>
                <ak-date-picker
                  v-model="app.startDate"
                  :custom-rules="startDateValidation"
                  :config="pickerConfig"
                />
              </ak-field>
              <ak-field label="End Date" class="ml-small">
                <template #helper>Optional end date</template>
                <ak-date-picker
                  v-model="app.endDate"
                  :custom-rules="endDateValidation"
                  :config="pickerConfig"
                />
              </ak-field>
            </div>
            <ak-field
              v-if="
                display('show', 'agencyuser,agencyadmin,superadmin,developer')
              "
            >
              <ak-checkbox v-model="app.hidden" label>
                <strong>Hidden Build</strong>
                <ak-tooltip class="pull-right">
                  <template #trigger>
                    <ak-tag ghost color="primary">Agency Only</ak-tag>
                  </template>
                  Only visible to Agency Users
                </ak-tooltip>
                <p class="mt-small">
                  <em
                    >Hide this page from the client while it is being created.
                    Once you're ready you can then make it visible to them from
                    the page editor.</em
                  >
                </p>
              </ak-checkbox>
            </ak-field>
          </template>
        </ak-form>
      </ak-box>
    </template>

    <template #body>
      <ak-box class="relative">
        <div
          v-if="!app.title || !app.campaign"
          v-show="!wizardLoading"
          class="overlay z-index--20"
        ></div>
        <ak-cards v-model="selectedLayout" centered>
          <template v-for="layout in layouts" :key="layout._id">
            <ak-cards-item class="app-card-wrapper">
              <template v-if="layout.name !== 'blank'">
                <ak-card
                  class="app-card"
                  :value="layout"
                  @click="createNewApp($event, layout)"
                >
                  <ak-loading
                    v-model:active="layout.creating"
                    :is-full-page="false"
                    :can-cancel="false"
                  />
                  <ak-card-image
                    class="h-275"
                    :hover-scroll="true"
                    :src="layout.preview"
                    @mouseover="($event) => (layout.showUseText = true)"
                    @mouseleave="($event) => (layout.showUseText = undefined)"
                  />
                  <ak-card-body
                    class="text-center relative"
                    :class="{
                      'bg-primary radius radius-top-none':
                        layout.showUseText === true,
                    }"
                    @mouseover="($event) => (layout.showUseText = true)"
                    @mouseleave="($event) => (layout.showUseText = undefined)"
                  >
                    <div v-if="layout.showUseText" class="color-white">
                      <strong
                        >Start with
                        <span class="text-capitalize">{{
                          layout.name
                        }}</span></strong
                      >
                    </div>
                    <div v-else>
                      <div
                        v-if="layout.version <= 3"
                        class="label mt-tiny mr-tiny absolute absolute--top-right ng-scope"
                      >
                        old
                      </div>
                      <h4
                        class="color-grey mb-none inline-block text-capitalize"
                      >
                        {{ layout.name }}
                      </h4>
                    </div>
                  </ak-card-body>
                </ak-card>
              </template>
              <template v-else>
                <ak-card
                  class="app-card empty-state empty-state--new card--selectable cursor-pointer"
                  value="customLayout"
                  @click="createNewApp($event, layout)"
                >
                  <ak-loading
                    v-model:active="layout.creating"
                    :is-full-page="false"
                    :can-cancel="false"
                  />
                  <ak-empty-state
                    icon="editor-banner"
                    title="Create your own"
                  />
                </ak-card>
              </template>
            </ak-cards-item>
          </template>
        </ak-cards>
      </ak-box>
    </template>
  </ak-sidebar-layout>
</template>

<script>
import SidebarLayout from '@akerolabs/akero-vue/src/components/sidebar/SidebarLayout.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Form from '@akerolabs/akero-vue/src/components/form/Form.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import Checkbox from '@akerolabs/akero-vue/src/components/checkbox/Checkbox.vue'
import DatePicker from '@akerolabs/akero-vue/src/components/datepicker/DatePicker.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Tag from '@akerolabs/akero-vue/src/components/tag/Tag.vue'
import Tooltip from '@akerolabs/akero-vue/src/components/tooltip/Tooltip.vue'
import Cards from '@akerolabs/akero-vue/src/components/cards/Cards.vue'
import CardsItem from '@akerolabs/akero-vue/src/components/cards/CardsItem.vue'
import Card from '@akerolabs/akero-vue/src/components/cards/Card.vue'
import CardImage from '@akerolabs/akero-vue/src/components/cards/CardImage.vue'
import CardBody from '@akerolabs/akero-vue/src/components/cards/CardBody.vue'
import EmptyState from '@akerolabs/akero-vue/src/components/emptyState/EmptyState.vue'
import Popover from '@akerolabs/akero-vue/src/components/popover/Popover.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import Pack from '@akerolabs/akero-vue/src/components/pack/Pack.vue'
import PackItem from '@akerolabs/akero-vue/src/components/pack/PackItem.vue'
import Loading from '@akerolabs/akero-vue/src/components/loading/Loading.vue'
import { defineComponent, onMounted, ref } from 'vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { accountObj, agencyObj, userObj } from '@akerolabs/akero-vue/src/store'
import { display } from '@akerolabs/akero-vue/src/services/users'
import { merge, hasOwn } from '@akerolabs/akero-vue/src/utils/helpers'
import date from '@akerolabs/akero-vue/src/utils/date'
import ProjectFormSelect from '../../projects/ProjectFormSelect.vue'
import orderBy from 'lodash/orderBy'

import { ignoreTimeZoneISO } from '@akerolabs/akero-vue/src/services/localisation'
import { sendEvent } from '../../../services/intercom.io'
import { LocalisationFactory } from '@akerolabs/akero-vue/src/services/localisation'

import {
  appSpecificOptionsFactory,
  appFormTemplatesFactory,
  appAutoresponseTemplatesFactory,
} from '../../../services/apps'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src'

import {
  uniqueValidtion,
  getStartEndValidation,
} from '../../../utils/validationRules'

import { useRouter } from '../../../legacy/router'

export default defineComponent({
  name: 'AkCreateApp',

  components: {
    [SidebarLayout.name]: SidebarLayout,
    [Box.name]: Box,
    [Form.name]: Form,
    [Field.name]: Field,
    [Input.name]: Input,
    [Checkbox.name]: Checkbox,
    [DatePicker.name]: DatePicker,
    [Button.name]: Button,
    [Tag.name]: Tag,
    [Tooltip.name]: Tooltip,
    [Cards.name]: Cards,
    [Card.name]: Card,
    [CardImage.name]: CardImage,
    [CardsItem.name]: CardsItem,
    [CardBody.name]: CardBody,
    [EmptyState.name]: EmptyState,
    [ProjectFormSelect.name]: ProjectFormSelect,
    [Popover.name]: Popover,
    [Icon.name]: Icon,
    [Pack.name]: Pack,
    [PackItem.name]: PackItem,
    [Loading.name]: Loading,
  },

  props: {
    appType: {
      type: String,
      default: 'promo',
    },

    campaignId: {
      type: String,
      default: undefined,
    },
  },

  setup(props) {
    const router = useRouter()

    const appSpecificOptions = appSpecificOptionsFactory()
    const appFormTemplates = appFormTemplatesFactory()
    const appAutoresponseTemplates = appAutoresponseTemplatesFactory()
    const localisation = LocalisationFactory()

    const upperLimit = date().startOf('Day').add(10, 'years').toISOString()
    const lowerLimit = date().startOf('Day').subtract(10, 'years').toISOString()
    const appTypes = ref(['promo', 'competition', 'voucher'])
    const showAdvancedOptions = ref(false)
    const layoutsLoading = ref(true)
    const account = ref({})
    const campaigns = ref([])
    const layouts = ref([])
    const minStartDate = ref(lowerLimit)
    const maxStartDate = ref(upperLimit)
    const minEndDate = ref(lowerLimit)
    const maxEndDate = ref(upperLimit)
    const allowNewApps = ref(false)
    const appQuotaLimitReached = ref(false)
    const selectedLayout = ref(undefined)
    const schema = ref(undefined)
    const wizardLoading = ref(true)
    const formVersion = ref(3)
    const minLimitStartDate = date().toISOString()
    const maxLimitEndDate = date().add(3, 'months').toISOString()
    const minLimitEndDate = minLimitStartDate
    const socialNetworks = ref(['facebook', 'linkedin', 'twitter'])
    const titleInput = ref(null)
    const sidebarForm = ref(null)
    const popper = ref(null)
    const sidebar = ref(null)
    let appCreating = false
    let newAppContent

    const socialUnicode = ref({
      facebook: 'icon-circlefacebook',
      linkedin: 'icon-circlelinkedin',
      twitter: 'icon-circletwitter',
    })

    const leadsConfig = ref({})
    leadsConfig.value.emails = []

    const customValidationRules = ref([
      uniqueValidtion({
        message: 'Name has already been used',
        type: props.appType,
        account: () => accountObj()._id,
        agency: () => agencyObj()._id,
        property: 'slug',
        slugify: true,
      }),
    ])

    const app = ref({
      title: '',
      campaign: '',
    })

    const toggleAdvanced = () => {
      showAdvancedOptions.value = !showAdvancedOptions.value
    }

    const fetchAccount = async () => {
      const { data } = await request.get(`/accounts/${accountObj()._id}`, {
        params: {
          agency: agencyObj()._id,
          overrideAccess: true,
        },
      })
      account.value = data.data
    }

    const fetchCampaigns = async () => {
      const { data } = await request.get(`/campaigns`, {
        params: {
          account: accountObj()._id,
          agency: agencyObj()._id,
          deleted: JSON.stringify({ $ne: true }),
          archived: JSON.stringify({ $ne: true }),
          per_page: 0,
        },
      })
      campaigns.value = data.data
    }

    const fetchLayouts = async () => {
      let version = JSON.stringify({ $gt: 3 })

      if (display('show', 'developer')) {
        version = JSON.stringify({ $gte: 3 })
      }

      const { data } = await request.get(`/layouts`, {
        params: {
          populate: 'theme',
          hidden: JSON.stringify({ $ne: true }),
          per_page: 0,
          version: version,
        },
      })

      layouts.value = orderBy(
        data.data,
        ['isNewLayout', 'order'],
        ['desc', 'desc'],
      )

      layoutsLoading.value = false
    }

    const fetchSchema = async () => {
      const { data } = await request.get('/schema/app')
      app.value = merge(app.value, data.data, true)
    }

    const setBasicAppDetails = () => {
      const { _id: userId } = userObj()
      const { name, _id: accountId } = accountObj()
      const { _id: agencyId } = agencyObj()

      app.value.layoutBuilder = true
      app.value.owner = userId
      app.value.agency = agencyId
      app.value.account = accountId
      app.value.accountName = name
      app.value.type = props.appType || 'promo'
    }

    const setCampaignId = (id) => {
      campaigns.value.find((c) => {
        if (c._id === id) {
          app.value.campaign = c._id
          app.value.campaignLabel = c.title
        }
      })
    }

    const selectCampaign = (campaign) => {
      app.value.campaign = campaign._id
    }

    const createNewApp = async (e, layout) => {
      if (appCreating) return

      appCreating = true
      layout.creating = true

      const isInvalid = await validateForm()

      if (isInvalid) {
        layout.creating = false
        appCreating = false
        return
      }

      const newApp = merge({}, app.value, true)

      newApp.startDate = ignoreTimeZoneISO(newApp.startDate)
      newApp.endDate = ignoreTimeZoneISO(newApp.endDate)
      newAppContent = {}

      selectLayout(newApp, layout)
        .then(addGlobalRecipients)
        .then(addSocialContent)
        .then(addAppContent)
        .then(fixForLayoutBuilder)
        .then(getAppSpecificOptions)
        .then(appSpecificForm)
        .then(appAutoResponse)
        .then(appAddLayoutBuilderStyles(layout))
        .then(saveNewApp)
    }

    const validateForm = async () => {
      const isValid = await sidebarForm.value.validate()
      return isValid.some((i) => i === false)
    }

    const selectLayout = (app, layout) => {
      return new Promise((resolve) => {
        app.appSpecificOptions = {}
        app.appVersion = 2
        app.layout = layout.name
        app.layoutId = layout._id
        app.layoutName = layout.title
        app.layoutVersion = layout.version
        app.live.blocks = layout.blocks || []
        app.draft.blocks = layout.blocks || []
        app.theme = layout.theme._id

        newAppContent.content = layout.content
        newAppContent.baseTheme = layout.theme._id
        newAppContent.styles = layout.theme.styles
        newAppContent.media = layout.theme.media

        try {
          app.hideHeader = layout.layoutOptions.layoutDefaults.hideHeader
          app.draft.hideHeader = layout.layoutOptions.layoutDefaults.hideHeader
        } catch (err) {
          app.hideHeader = true
          app.draft.hideHeader = true
        }

        if (typeof layout.appSpecificOptions !== 'undefined') {
          merge(app.appSpecificOptions, layout.appSpecificOptions)
        }
        resolve(app)
      })
    }

    const addGlobalRecipients = (app) => {
      return new Promise((resolve) => {
        if (!Array.isArray(app.leadsConfig.leadsEmails)) {
          app.leadsConfig.leadsEmails = []
        }

        function forLoopLogic(item) {
          try {
            const idx = app.leadsConfig.leadsEmails.findIndex((i) => i === item)
            if (idx >= 0) {
              app.leadsConfig.leadsEmails.push(item)
            }
          } catch (ex) {
            console.log(ex)
          }
        }

        for (
          var i = 0, l = account.value.settings.leads.globalRecipients.length;
          i < l;
          i += 1
        ) {
          forLoopLogic(account.value.settings.leads.globalRecipients[i])
        }

        resolve(app)
      })
    }

    function addSocialContent(app) {
      return new Promise((resolve) => {
        const networks = socialNetworks.value
        const acc = account.value
        newAppContent.social = []
        newAppContent.website = {}
        newAppContent.telephone = {}

        function addSocialLink(idx) {
          newAppContent.social[networks[idx]] = {
            service: networks[idx],
            unicode: socialUnicode.value[networks[idx]],
            url: acc.contactDetails[networks[idx]],
          }
          newAppContent.website = {
            url: acc.contactDetails.website,
            text: acc.contactDetails.website,
          }
          newAppContent.telephone = {
            url: acc.contactDetails.telephone,
            text: acc.contactDetails.telephone,
          }
        }

        for (let i = 0; i < networks.length; i += 1) {
          if (
            typeof acc.contactDetails[networks[i]] !== 'undefined' &&
            acc.contactDetails[networks[i]] !== ''
          ) {
            addSocialLink(i)
          }
        }
        resolve(app)
      })
    }

    function addAppContent(app) {
      return new Promise((resolve) => {
        for (let key in newAppContent) {
          if (hasOwn(newAppContent, key)) {
            app.draft[key] = newAppContent[key]
            app.live[key] = newAppContent[key]
          }
        }
        resolve(app)
      })
    }

    function fixForLayoutBuilder(app) {
      return new Promise((resolve) => {
        if (app.layoutBuilder) {
          delete app.draft.formLayout
          delete app.live.formLayout
          delete app.trackingConfig
        }
        resolve(app)
      })
    }

    function getAppSpecificOptions(app) {
      return new Promise((resolve) => {
        appSpecificOptions.get(app.type, (options) => {
          merge(app.appSpecificOptions, options)
          resolve(app)
        })
      })
    }

    function appSpecificForm(app) {
      return new Promise((resolve) => {
        appFormTemplates.get(app.type, (form) => {
          form.postEnabled = false
          form.postContent = '<p>View our [[PRIVACY_POLICY_LINK]]</p>'
          form.version = formVersion
          app.draft.formLayout = form
          app.live.formLayout = form
          resolve(app)
        })
      })
    }

    function appAutoResponse(app) {
      return new Promise((resolve) => {
        appAutoresponseTemplates.get(app.type, (template) => {
          app.leadsConfig.autoresponseTemplate = template.email
          app.leadsConfig.autoresponseSMSTemplate = template.sms
          app.fbConfig.fbShareName = app.title
          app.fbConfig.fbShareDescription = 'Share this with your friends'
          resolve(app)
        })
      })
    }

    function appAddLayoutBuilderStyles(layout) {
      return (app) => {
        return new Promise((resolve) => {
          if (Object.keys(layout.layoutOptions).length && app.layoutBuilder) {
            try {
              // Copy the fonts to the new app
              app.draft.styles.primaryFont =
                layout.layoutOptions.styles.primaryFont
              app.draft.styles.secondaryFont =
                layout.layoutOptions.styles.secondaryFont
              app.draft.styles.tertiaryFont =
                layout.layoutOptions.styles.tertiaryFont
              // Copy the font settings to the new app
              app.draft.styles.heading1Size =
                layout.layoutOptions.styles.heading1Size
              app.draft.styles.heading2Size =
                layout.layoutOptions.styles.heading2Size
              app.draft.styles.heading3Size =
                layout.layoutOptions.styles.heading3Size
              app.draft.styles.heading4Size =
                layout.layoutOptions.styles.heading4Size
              app.draft.styles.heading5Size =
                layout.layoutOptions.styles.heading5Size
              app.draft.styles.heading6Size =
                layout.layoutOptions.styles.heading6Size
              // Copy the line height settings to the new app
              app.draft.styles.heading1LineHeight =
                layout.layoutOptions.styles.heading1LineHeight
              app.draft.styles.heading2LineHeight =
                layout.layoutOptions.styles.heading2LineHeight
              app.draft.styles.heading3LineHeight =
                layout.layoutOptions.styles.heading3LineHeight
              app.draft.styles.heading4LineHeight =
                layout.layoutOptions.styles.heading4LineHeight
              app.draft.styles.heading5LineHeight =
                layout.layoutOptions.styles.heading5LineHeight
              app.draft.styles.heading6LineHeight =
                layout.layoutOptions.styles.heading6LineHeight
            } catch (e) {
              /* istanbul ignore next */
            }
          }
          resolve(app)
        })
      }
    }

    const saveNewApp = async (app) => {
      const { data: res } = await request.post('/apps', app)
      const { data, status } = res

      if (status === 'success') {
        sendEvent({
          eventName: 'completed-app-wizard',
          metaData: {
            app_id: data._id,
            app_name: data.title,
            // appsUrl defined in admin html script tag
            app_url: `${appsUrl}/${data.account.slug}/${data.slug}`,
            app_type: data.type,
          },
        })

        router.push('campaigns.editorAlt.edit', {
          editAppId: data._id,
          agencyId: agencyObj()._id,
          accountSlug: accountObj().slug,
        })
      } else {
        ToastProgrammatic.open({
          duration: 5000,
          type: 'danger',
          message: 'Failed to create app',
        })
      }
    }

    const { startDateValidation, endDateValidation } = getStartEndValidation({
      startDate: () => app.value.startDate,
      endDate: () => app.value.endDate,
    })

    const pickerConfig = {
      altInput: true,
      altFormat: localisation.getLocalisationPreferences().flatPickrFormat,
    }

    onMounted(async () => {
      await fetchAccount()
      await fetchCampaigns()
      await fetchLayouts()
      await fetchSchema()
      setBasicAppDetails()
      setCampaignId(props.campaignId)
      titleInput.value.focus()
      wizardLoading.value = false
      popper.value.open()
    })

    return {
      appTypes,
      showAdvancedOptions,
      toggleAdvanced,
      layoutsLoading,
      app,
      account,
      campaigns,
      layouts,
      display,
      minStartDate,
      maxStartDate,
      minEndDate,
      maxEndDate,
      allowNewApps,
      appQuotaLimitReached,
      selectedLayout,
      schema,
      selectCampaign,
      socialNetworks,
      wizardLoading,
      formVersion,
      minLimitStartDate,
      maxLimitEndDate,
      minLimitEndDate,
      leadsConfig,
      socialUnicode,
      createNewApp,
      titleInput,
      sidebarForm,
      sidebar,
      popper,
      customValidationRules,
      startDateValidation,
      endDateValidation,
      pickerConfig,
    }
  },
})
</script>

<style lang="scss">
@import '~@akerolabs/akero-vue/src/scss/_settings.scss';
.create-app {
  .ak-sidebar-layout__sidebar {
    z-index: 100;
  }
  .ak-sidebar-layout__body {
    overflow-y: auto;
  }

  .card-body {
    &.is-hovered {
      display: block;
      background-color: $color-primary;
      color: white;
    }
  }
}
</style>
